import React from 'react';
import {
    AutocompleteInput,
    BooleanField,
    BooleanInput,
    Create,
    Datagrid,
    DateField,
    DisabledInput,
    Edit,
    Filter,
    FormDataConsumer,
    FunctionField,
    List,
    NumberInput,
    ReferenceInput,
    required,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
} from 'react-admin';
import { DateTimeInput } from 'react-admin-date-inputs';
import {
    BulkDeleteButtonAdmin,
    CustomPagination,
    isEmployee,
    isFranchisee,
    isGeEmployee,
    isPartner,
    isPartnerService,
    stylesWide,
    ToolbarSaveAllDeleteAdmin,
} from './utils';
import { withStyles } from '@material-ui/core/styles';
import { getPosAppVersion } from './constants/stations';
import { stationModels } from './constants/stationModels';
import { LocationInfoField, LOCATIONS_INFO_RESOURCE } from './Locations/components/Info';
import { locationInfoRenderer } from './Locations/utils';

const StationsFilter = props => (
    <Filter {...props}>
        <SelectInput
            label="Поле"
            source="field"
            choices={[
                { id: 'sno', name: 'Sno' },
                { id: 'location_id', name: 'Id локации' },
                { id: 'owner_type', name: 'Тип контракта' },
                { id: 'owner_contract_id', name: 'Id контракта' },
                { id: 'model', name: 'Модель' },
                { id: 'qr', name: 'Qr-код' },
                { id: 'holder_prefix', name: 'Префикс' },
                { id: 'holder_short_number', name: 'Короткий номер' },
                { id: 'status', name: 'Статус' },
                { id: 'terminal_id', name: 'Терминал' },
                { id: 'last_message', name: 'Последнее сообщение' },
                { id: 'created', name: 'Время создания' },
                { id: 'updated', name: 'Последнее обновление' },
            ]}
            alwaysOn
        />
        <TextInput
            label="Поиск"
            source="q"
            alwaysOn
        />
    </Filter>
);

export const ContractIdField = ({ record = {} }) => {
    if (record.ff_contract && record.ff_contract.franchisee)
        return (
            <span>
                {record.ff_contract.id} ({record.ff_contract.franchisee.name})
            </span>
        );
    else if (record.partner_service_contract && record.partner_service_contract.partner_service)
        return (
            <span>
                {record.partner_service_contract.id} (
                {record.partner_service_contract.partner_service.name})
            </span>
        );
    return <span>{record.owner_contract_id}</span>;
};

ContractIdField.defaultProps = { label: 'Id контракта' };

const OwnershipField = ({ record = {} }) => (
    <span>{record.has_direct_access === true ? 'Direct' : 'Share'}</span>
);

OwnershipField.defaultProps = { label: 'Владение' };

const canChangeStationSlotCapacity = station => {
    return (
        station.sno &&
        station.model == stationModels.relink_station &&
        Number(station.sno.substring(4, 6)) == 6
    );
};

export const StationsList = ({ permissions, ...props }) => (
    <List
        {...props}
        filters={<StationsFilter />}
        bulkActionButtons={
            !isPartner(permissions) ? <BulkDeleteButtonAdmin permissions={permissions} /> : false
        }
        pagination={<CustomPagination />}
    >
        <Datagrid
            rowClick={(id, basePath, record) =>
                permissions === 'partner_service' && (!record || !record.has_direct_access)
                    ? false
                    : 'edit'
            }
        >
            <TextField
                label="Sno"
                source="sno"
            />
            {isPartnerService(permissions) && <OwnershipField source="has_direct_access" />}
            <LocationInfoField
                label="Локация"
                source="location_id"
                render={(value, location) => `${location.id} (${value})`}
            />
            {isEmployee(permissions) && (
                <ContractIdField
                    label="Id контракта"
                    source="owner_contract_id"
                />
            )}
            {!isPartner(permissions) && (
                <TextField
                    label="SIM"
                    source="sim"
                />
            )}
            {!isPartner(permissions) && (
                <TextField
                    label="Модель"
                    source="model"
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <TextField
                    label="Дизайн"
                    source="design"
                />
            )}
            {!isPartner(permissions) && (
                <TextField
                    label="QR"
                    source="station_qr"
                />
            )}
            {!isPartner(permissions) && (
                <TextField
                    label="Префикс"
                    source="holder_prefix"
                />
            )}
            <TextField
                label="Короткий номер"
                source="holder_short_number"
            />
            <TextField
                label="Статус"
                source="status"
            />
            <TextField
                label="Количество слотов"
                source="capacity"
            />
            {isGeEmployee(permissions) && (
                <BooleanField
                    label="Виртуальная"
                    source="is_virtual"
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <TextField
                    label="Терминал"
                    source="terminal_id"
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <FunctionField
                    label="POS"
                    render={record => getPosAppVersion(record.posapp_version)}
                />
            )}
            <DateField
                label="Последнее сообщение"
                source="last_message"
                showTime
                locales="ru-RU"
            />
            {isEmployee(permissions) && (
                <DateField
                    label="Total mismatch"
                    source="total_mismatch"
                    showTime
                    locales="ru-RU"
                />
            )}
            {!isPartner(permissions) && (
                <DateField
                    label="Время создания"
                    source="created"
                    showTime
                    locales="ru-RU"
                />
            )}
            {!isPartner(permissions) && (
                <DateField
                    label="Последнее обновление"
                    source="updated"
                    showTime
                    locales="ru-RU"
                />
            )}
        </Datagrid>
    </List>
);

const filterToQuery = searchText => ({ q: searchText, field: 'name' });

export const AddStation = withStyles(stylesWide)(({ classes, permissions, ...props }) => (
    <Create
        classes={classes}
        {...props}
    >
        <SimpleForm classes={classes}>
            <TextInput
                label="Sno"
                source="sno"
                validate={required()}
                className={classes.wide}
            />
            <ReferenceInput
                perPage={10}
                label="Локация"
                source="location_id"
                reference={LOCATIONS_INFO_RESOURCE}
                filter={{
                    free_loc: !isEmployee(permissions),
                }}
                filterToQuery={filterToQuery}
                allowEmpty
                className={classes.wide}
            >
                <AutocompleteInput
                    className={classes.wide}
                    optionText={locationInfoRenderer}
                />
            </ReferenceInput>
            {isEmployee(permissions) && (
                <>
                    <SelectInput
                        label="Тип пользователя"
                        source="owner_type"
                        choices={[
                            { id: 'ff_contract', name: 'Контракт франчайзи' },
                            { id: 'partner_service', name: 'Контракт сервисного партнера' },
                        ]}
                        className={classes.wide}
                    />
                    <div>
                        <FormDataConsumer>
                            {({ formData, ...rest }) =>
                                formData.owner_type === 'ff_contract' ? (
                                    <ReferenceInput
                                        key={formData.owner_type}
                                        perPage={10000}
                                        label="Id контракта франчайзи"
                                        source="owner_contract_id"
                                        reference="ff_contracts"
                                        allowEmpty
                                        parse={value => value || null}
                                        className={classes.wide}
                                    >
                                        <SelectInput optionText={data => `${data.id}`} />
                                    </ReferenceInput>
                                ) : (
                                    <ReferenceInput
                                        key={formData.owner_type}
                                        perPage={10000}
                                        label="Id контракта сервисного партнера"
                                        source="owner_contract_id"
                                        reference="partner_service_contracts"
                                        allowEmpty
                                        parse={value => value || null}
                                        className={classes.wide}
                                    >
                                        <SelectInput optionText={data => `${data.id}`} />
                                    </ReferenceInput>
                                )
                            }
                        </FormDataConsumer>
                    </div>
                </>
            )}
            <TextInput
                label="Модель"
                source="model"
                className={classes.wide}
            />
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <SelectInput
                    label="Дизайн"
                    source="design"
                    choices={[
                        { id: 'GE', name: 'GE' },
                        { id: 'UR-GE', name: 'UR-GE' },
                        { id: 'UR', name: 'UR' },
                    ]}
                    className={classes.wide}
                />
            )}
            <TextInput
                label="SIM"
                source="sim"
                className={classes.wide}
            />
            <TextInput
                label="QR"
                source="station_qr"
                allowEmpty
                parse={value => value}
                className={classes.wide}
            />
            <TextInput
                disabled={isPartnerService(permissions)}
                label="Префикс"
                source="holder_prefix"
                allowEmpty
                parse={value => value}
                className={classes.wide}
            />
            {/*//TODO*/}
            {/*<ReferenceInput*/}
            {/*    perPage={100}*/}
            {/*    label="Префикс"*/}
            {/*    source="holder_prefix"*/}
            {/*    reference="station_holders"*/}
            {/*    allowEmpty*/}
            {/*    parse={value => value}*/}
            {/*    queryOptions={{ meta: { findBy: 'prefix' } }}*/}
            {/*>*/}
            {/*    <SelectInput optionText={data => `${data.prefix}`} />*/}
            {/*</ReferenceInput>*/}
            <TextInput
                label="Короткий номер"
                source="holder_short_number"
                className={classes.wide}
            />
            <SelectInput
                label="Статус"
                source="status"
                choices={[
                    { id: 'online', name: 'online' },
                    { id: 'offline', name: 'offline' },
                    { id: 'pre_offline', name: 'pre_offline' },
                ]}
                className={classes.wide}
            />
            <NumberInput
                label="Количество слотов"
                source="capacity"
                className={classes.wide}
            />
            {isGeEmployee(permissions) && (
                <BooleanInput
                    label="Виртуальная"
                    source="is_virtual"
                    className={classes.wide}
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <TextInput
                    label="Терминал"
                    source="terminal_id"
                    className={classes.wide}
                />
            )}
        </SimpleForm>
    </Create>
));

export const EditStation = withStyles(stylesWide)(({ classes, permissions, ...props }) => (
    <Edit
        classes={classes}
        {...props}
    >
        <SimpleForm
            classes={classes}
            toolbar={<ToolbarSaveAllDeleteAdmin permissions={permissions} />}
        >
            <TextInput
                disabled
                label="Id"
                source="id"
                className={classes.wide}
            />
            <TextInput
                disabled
                label="Sno"
                source="sno"
                className={classes.wide}
            />
            <ReferenceInput
                perPage={10}
                label="Локация"
                source="location_id"
                reference={LOCATIONS_INFO_RESOURCE}
                filter={{
                    free_loc: !isEmployee(permissions),
                }}
                filterToQuery={filterToQuery}
                allowEmpty
                className={classes.wide}
            >
                <AutocompleteInput
                    optionText={locationInfoRenderer}
                    className={classes.wide}
                />
            </ReferenceInput>

            {isEmployee(permissions) && (
                <>
                    <SelectInput
                        label="Тип пользователя"
                        source="owner_type"
                        choices={[
                            { id: 'ff_contract', name: 'Контракт франчайзи' },
                            { id: 'partner_service', name: 'Контракт сервисного партнера' },
                        ]}
                        className={classes.wide}
                    />
                    <div>
                        <FormDataConsumer>
                            {({ formData, ...rest }) =>
                                formData.owner_type === 'ff_contract' ? (
                                    <ReferenceInput
                                        key={formData.owner_type}
                                        perPage={10000}
                                        label="Id контракта франчайзи"
                                        source="owner_contract_id"
                                        reference="ff_contracts"
                                        allowEmpty
                                        parse={value => value || null}
                                        className={classes.wide}
                                    >
                                        <SelectInput
                                            className={classes.wide}
                                            optionText={data => `${data.id}`}
                                        />
                                    </ReferenceInput>
                                ) : (
                                    <ReferenceInput
                                        key={formData.owner_type}
                                        perPage={10000}
                                        label="Id контракта сервисного партнера"
                                        source="owner_contract_id"
                                        reference="partner_service_contracts"
                                        allowEmpty
                                        parse={value => value || null}
                                        className={classes.wide}
                                    >
                                        <SelectInput
                                            className={classes.wide}
                                            optionText={data => `${data.id}`}
                                        />
                                    </ReferenceInput>
                                )
                            }
                        </FormDataConsumer>
                    </div>
                </>
            )}
            <TextInput
                disabled={isPartnerService(permissions)}
                label="Модель"
                source="model"
                className={classes.wide}
            />
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <SelectInput
                    label="Дизайн"
                    source="design"
                    choices={[
                        { id: 'GE', name: 'GE' },
                        { id: 'UR-GE', name: 'UR-GE' },
                        { id: 'UR', name: 'UR' },
                    ]}
                    className={classes.wide}
                />
            )}
            <TextInput
                disabled={isPartnerService(permissions)}
                label="SIM"
                source="sim"
                className={classes.wide}
            />
            <TextInput
                // disabled={isPartnerService(permissions)}
                label="QR"
                source="station_qr"
                allowEmpty
                parse={value => value}
                className={classes.wide}
            />
            <TextInput
                disabled={isPartnerService(permissions)}
                label="Префикс"
                source="holder_prefix"
                allowEmpty
                parse={value => value}
                className={classes.wide}
            />
            {/*//TODO*/}
            {/*<ReferenceInput*/}
            {/*    disabled={isPartnerService(permissions)}*/}
            {/*    perPage={100}*/}
            {/*    label="Префикс"*/}
            {/*    source="holder_prefix"*/}
            {/*    reference="station_holders"*/}
            {/*    allowEmpty*/}
            {/*    parse={value => value}*/}
            {/*    queryOptions={{ meta: { findBy: 'prefix' } }}*/}
            {/*>*/}
            {/*    <SelectInput optionText={data => `${data.prefix}`} />*/}
            {/*</ReferenceInput>*/}
            <TextInput
                disabled={isPartnerService(permissions)}
                label="Короткий номер"
                source="holder_short_number"
                className={classes.wide}
            />
            <SelectInput
                disabled={isPartnerService(permissions)}
                label="Статус"
                source="status"
                choices={[
                    { id: 'online', name: 'online' },
                    { id: 'offline', name: 'offline' },
                    { id: 'pre_offline', name: 'pre_offline' },
                ]}
                className={classes.wide}
            />
            <FormDataConsumer>
                {({ record }) =>
                    record && canChangeStationSlotCapacity(record) ? (
                        <SelectInput
                            label="Количество слотов"
                            source="capacity"
                            choices={[
                                { id: 6, name: '6' },
                                { id: 12, name: '12' },
                                { id: 18, name: '18' },
                            ]}
                            className={classes.wide}
                        />
                    ) : (
                        <TextInput
                            label="Количество слотов"
                            source="capacity"
                            className={classes.wide}
                            disabled
                        />
                    )
                }
            </FormDataConsumer>
            {isGeEmployee(permissions) && (
                <BooleanInput
                    label="Виртуальная"
                    source="is_virtual"
                    className={classes.wide}
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <TextInput
                    label="Терминал"
                    source="terminal_id"
                    className={classes.wide}
                />
            )}
            {(isEmployee(permissions) || isFranchisee(permissions)) && (
                <DisabledInput
                    label="POS"
                    source="posapp_version"
                    format={getPosAppVersion}
                    className={classes.wide}
                />
            )}
            <DateTimeInput
                disabled={isPartnerService(permissions)}
                label="Последнее сообщение"
                source="last_message"
                options={{ format: 'dd.MM.YYYY HH:mm', ampm: false }}
                className={classes.wide}
            />
            {isEmployee(permissions) && (
                <DateTimeInput
                    label="Total mismatch"
                    source="total_mismatch"
                    options={{ format: 'dd.MM.YYYY HH:mm', ampm: false }}
                    className={classes.wide}
                />
            )}
            <TextInput
                disabled
                label="Время создания"
                source="created"
                showTime
                className={classes.wide}
            />
            <TextInput
                disabled
                label="Последнее обновление"
                source="updated"
                showTime
                className={classes.wide}
            />
        </SimpleForm>
    </Edit>
));
